import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/ManageCertificates.css";

// API endpoint for fetching certificates (to be used later with backend integration)
const API_URL = "/api/certificates"; // Replace with actual backend URL when attached

// Dummy data for certificates (to be replaced with API fetch)
const dummyCertificates = [
  { id: 1, recipient: "John Doe", email: "john@example.com", status: "Issued", dateIssued: "2025-05-01", expirationDate: "2026-05-01", type: "Flanschmonteur", location: "Berlin" },
  { id: 2, recipient: "Jane Smith", email: "jane@example.com", status: "Revoked", dateIssued: "2025-04-15", expirationDate: "2026-04-15", type: "PSAgA", location: "Munich" },
  { id: 3, recipient: "Alice Brown", email: "alice@example.com", status: "Issued", dateIssued: "2025-03-20", expirationDate: "2026-03-20", type: "Hubarbeitsbühnen", location: "Hamburg" },
  { id: 4, recipient: "Bob Johnson", email: "bob@example.com", status: "Expired", dateIssued: "2024-12-10", expirationDate: "2025-12-10", type: "Namensschild", location: "Berlin" },
];

// Status display mapping for German translations
const statusDisplay = {
  Issued: "Ausgestellt",
  Revoked: "Widerrufen",
  Expired: "Abgelaufen",
};

const ManageCertificates = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [locationFilter, setLocationFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const pageSize = 10;

  // Fetch data (use dummy for now, replace with API call later)
  useEffect(() => {
    // Simulate API fetch
    // async function fetchData() {
    //   try {
    //     const response = await fetch(API_URL);
    //     const data = await response.json();
    //     setCertificates(data);
    //   } catch (error) {
    //     console.error("Error fetching certificates:", error);
    //   } finally {
    //     setLoading(false);
    //   }
    // }
    // fetchData();
    setCertificates(dummyCertificates);
    setLoading(false);
  }, []);

  // Get unique types and locations for filters
  const uniqueTypes = [...new Set(certificates.map((cert) => cert.type))];
  const uniqueLocations = [...new Set(certificates.map((cert) => cert.location))];

  // Handle search, filter, and sort
  const filteredCertificates = certificates.filter((cert) => {
    const matchesSearch =
      cert.recipient.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.id.toString().includes(searchTerm);
    const matchesStatus = statusFilter === "All" || cert.status === statusFilter;
    const matchesType = typeFilter === "All" || cert.type === typeFilter;
    const matchesLocation = locationFilter === "All" || cert.location === locationFilter;
    const issuedDate = new Date(cert.dateIssued);
    const matchesFromDate = !fromDate || issuedDate >= new Date(fromDate);
    const matchesToDate = !toDate || issuedDate <= new Date(toDate);
    return matchesSearch && matchesStatus && matchesType && matchesLocation && matchesFromDate && matchesToDate;
  });

  const sortedCertificates = [...filteredCertificates].sort((a, b) => {
    let valA = a[sortBy];
    let valB = b[sortBy];
    if (sortBy === "dateIssued" || sortBy === "expirationDate") {
      valA = new Date(valA);
      valB = new Date(valB);
    }
    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedCertificates = sortedCertificates.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const totalPages = Math.ceil(sortedCertificates.length / pageSize);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  // Handle selection for bulk actions
  const handleSelectCertificate = (id) => {
    if (selectedCertificates.includes(id)) {
      setSelectedCertificates(selectedCertificates.filter((certId) => certId !== id));
    } else {
      setSelectedCertificates([...selectedCertificates, id]);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCertificates(paginatedCertificates.map((cert) => cert.id));
    } else {
      setSelectedCertificates([]);
    }
  };

  // Bulk actions (placeholders)
  const handleBulkExport = () => {
    if (selectedCertificates.length === 0) {
      alert("Bitte wählen Sie mindestens ein Zertifikat aus.");
      return;
    }
    alert(`Exportiere ${selectedCertificates.length} Zertifikate... (Platzhalter)`);
  };

  const handleBulkDelete = () => {
    if (selectedCertificates.length === 0) {
      alert("Bitte wählen Sie mindestens ein Zertifikat aus.");
      return;
    }
    if (window.confirm(`Möchten Sie ${selectedCertificates.length} Zertifikate wirklich löschen?`)) {
      setCertificates(certificates.filter((cert) => !selectedCertificates.includes(cert.id)));
      setSelectedCertificates([]);
      alert("Zertifikate gelöscht (Platzhalter).");
    }
  };

  // Individual actions (placeholders)
  const handleView = (id) => {
    alert(`Zeige Details für Zertifikat mit ID ${id}... (Platzhalter)`);
    setOpenDropdownId(null);
    // Later: navigate(`/view-certificate/${id}`);
  };

  const handleEdit = (id) => {
    alert(`Bearbeite Zertifikat mit ID ${id}... (Platzhalter)`);
    setOpenDropdownId(null);
    // Later: navigate(`/edit-certificate/${id}`);
  };

  const handleDownload = (id) => {
    alert(`Lade Zertifikat mit ID ${id} herunter... (Platzhalter)`);
    setOpenDropdownId(null);
  };

  const handleResend = (id) => {
    alert(`Sende Zertifikat mit ID ${id} erneut... (Platzhalter)`);
    setOpenDropdownId(null);
  };

  const handleRevoke = (id) => {
    if (window.confirm("Möchten Sie dieses Zertifikat wirklich widerrufen?")) {
      setCertificates(
        certificates.map((cert) =>
          cert.id === id ? { ...cert, status: "Revoked" } : cert
        )
      );
      alert("Zertifikat widerrufen (Platzhalter).");
    }
    setOpenDropdownId(null);
  };

  const handleRenew = (id) => {
    if (window.confirm("Möchten Sie dieses Zertifikat wirklich erneuern?")) {
      const today = new Date().toISOString().slice(0, 10);
      const nextYear = new Date();
      nextYear.setFullYear(nextYear.getFullYear() + 1);
      const expiration = nextYear.toISOString().slice(0, 10);
      setCertificates(
        certificates.map((cert) =>
          cert.id === id ? { ...cert, status: "Issued", dateIssued: today, expirationDate: expiration } : cert
        )
      );
      alert("Zertifikat erneuert (Platzhalter).");
    }
    setOpenDropdownId(null);
  };

  if (loading) {
    return <div className="loading">Zertifikate werden geladen...</div>;
  }

  return (
    <div className="manage-certificates-container">
      <header className="manage-certificates-header">
        <h1>Zertifikate verwalten</h1>
      </header>
      <main className="manage-certificates-main">
        {/* Filter Section - Improved layout for compactness */}
        <section className="filter-section">
          <div className="filter-group">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Nach ID, Name, Typ, Ort oder E-Mail suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="type-filter">
              <label>Typ:</label>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="All">Alle Typen</option>
                {uniqueTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="location-filter">
              <label>Ort:</label>
              <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                <option value="All">Alle Orte</option>
                {uniqueLocations.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>
            <div className="date-filter">
              <label>Ausstellungsdatum von:</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              <label>bis:</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
        </section>

        {/* Status Tabs */}
        <section className="status-tabs-section">
          <div className="status-tabs">
            <button
              className={statusFilter === "All" ? "active" : ""}
              onClick={() => setStatusFilter("All")}
            >
              Alle
            </button>
            <button
              className={statusFilter === "Issued" ? "active" : ""}
              onClick={() => setStatusFilter("Issued")}
            >
              Ausgestellt
            </button>
            <button
              className={statusFilter === "Revoked" ? "active" : ""}
              onClick={() => setStatusFilter("Revoked")}
            >
              Widerrufen
            </button>
            <button
              className={statusFilter === "Expired" ? "active" : ""}
              onClick={() => setStatusFilter("Expired")}
            >
              Abgelaufen
            </button>
          </div>
        </section>

        {/* Bulk Actions */}
        <section className="bulk-actions-section">
          <div className="bulk-actions">
            <button onClick={handleBulkExport}>Exportieren</button>
            <button onClick={handleBulkDelete}>Löschen</button>
          </div>
          <p>Ausgewählt: {selectedCertificates.length} | Gesamt: {sortedCertificates.length}</p>
        </section>

        {/* Certificates Table */}
        <section className="certificates-table-section">
          <table className="certificates-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={
                      paginatedCertificates.length > 0 &&
                      selectedCertificates.length === paginatedCertificates.length
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort("id")}>
                  ID {sortBy === "id" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("recipient")}>
                  Empfänger {sortBy === "recipient" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("email")}>
                  E-Mail {sortBy === "email" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("status")}>
                  Status {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("dateIssued")}>
                  Ausstellungsdatum {sortBy === "dateIssued" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("expirationDate")}>
                  Verfallsdatum {sortBy === "expirationDate" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("type")}>
                  Typ {sortBy === "type" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("location")}>
                  Ort {sortBy === "location" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCertificates.length > 0 ? (
                paginatedCertificates.map((cert) => (
                  <tr key={cert.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedCertificates.includes(cert.id)}
                        onChange={() => handleSelectCertificate(cert.id)}
                      />
                    </td>
                    <td>{cert.id}</td>
                    <td>{cert.recipient}</td>
                    <td>{cert.email}</td>
                    <td className={`status-${cert.status.toLowerCase()}`}>
                      {statusDisplay[cert.status] || cert.status}
                    </td>
                    <td>{cert.dateIssued}</td>
                    <td>{cert.expirationDate}</td>
                    <td>{cert.type}</td>
                    <td>{cert.location}</td>
                    <td className="action-buttons">
                      <button
                        className="action-btn"
                        onClick={() =>
                          setOpenDropdownId(openDropdownId === cert.id ? null : cert.id)
                        }
                      />
                      {openDropdownId === cert.id && (
                        <div className="action-dropdown">
                          <button className="view" onClick={() => handleView(cert.id)}>
                            Anzeigen
                          </button>
                          <button className="edit" onClick={() => handleEdit(cert.id)}>
                            Bearbeiten
                          </button>
                          <button className="download" onClick={() => handleDownload(cert.id)}>
                            Herunterladen
                          </button>
                          <button className="resend" onClick={() => handleResend(cert.id)}>
                            Erneut senden
                          </button>
                          {cert.status === "Issued" && (
                            <button className="revoke" onClick={() => handleRevoke(cert.id)}>
                              Widerrufen
                            </button>
                          )}
                          {cert.status === "Expired" && (
                            <button className="renew" onClick={() => handleRenew(cert.id)}>
                              Erneuern
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10">Keine Zertifikate gefunden.</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
              Vorherige
            </button>
            <span>Seite {currentPage} von {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              Nächste
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageCertificates;